import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import axios from 'axios';
import { User, Trophy, Target, Clock, TrendingUp, Coins, Award, BookOpen, Copy, Check, Pencil, X, Save, Trash2, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { user, refreshUser, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isGoogleUser = user?.auth_provider === 'google';

  const handleDeleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/auth/account`, {
        data: { confirm: deleteConfirm.trim(), password: deletePassword || null },
      });
      toast.success('Your account has been permanently deleted.');
      localStorage.removeItem('aptivon_token');
      await logout();
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/profile/stats`);
      setStats(data);
    } catch (err) {
      toast.error('Failed to load profile stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSaveName = async () => {
    if (!newName.trim() || newName.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/profile/update`, { name: newName.trim() });
      toast.success('Name updated!');
      setEditing(false);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setSaving(false);
    }
  };

  const copyReferral = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const statCards = stats ? [
    { label: 'Quizzes Played', value: stats.total_quizzes, icon: Target, color: '#0EA5E9' },
    { label: 'Wins', value: stats.total_wins, icon: Trophy, color: '#FBBF24' },
    { label: 'Win Rate', value: `${stats.win_rate}%`, icon: TrendingUp, color: '#10B981' },
    { label: 'Avg Score', value: `${stats.avg_score}%`, icon: Award, color: '#8B5CF6' },
    { label: 'Best Score', value: `${stats.best_score}%`, icon: Target, color: '#FF6B6B' },
    { label: 'Best Rank', value: stats.best_rank ? `#${stats.best_rank}` : '-', icon: Trophy, color: '#F59E0B' },
    { label: 'Points Earned', value: stats.total_earned.toLocaleString(), icon: Coins, color: '#10B981' },
    { label: 'Points Spent', value: stats.total_spent.toLocaleString(), icon: Coins, color: '#EF4444' },
    { label: 'Practice Quizzes', value: stats.practice_quizzes, icon: BookOpen, color: '#8B5CF6' },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Profile Header Card */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm p-6 sm:p-8 mb-8" data-testid="profile-header">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center shrink-0 shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #EF4444, #B91C1C)' }} data-testid="profile-avatar">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl sm:text-3xl font-black text-white">{initials}</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              {editing ? (
                <div className="flex items-center gap-2 mb-2 justify-center sm:justify-start">
                  <input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    className="px-3 py-2 border-2 border-[#0EA5E9] rounded-xl font-bold text-[#111827] focus:outline-none text-lg"
                    autoFocus
                    data-testid="edit-name-input"
                  />
                  <button onClick={handleSaveName} disabled={saving}
                    className="p-2 bg-[#10B981] text-white rounded-xl hover:bg-emerald-600 transition-colors"
                    data-testid="save-name-button"
                  ><Save className="w-4 h-4" /></button>
                  <button onClick={() => setEditing(false)}
                    className="p-2 bg-slate-200 text-[#6B7280] rounded-xl hover:bg-slate-300 transition-colors"
                  ><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-1 justify-center sm:justify-start">
                  <h1 className="text-2xl sm:text-3xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="profile-name">
                    {user?.name}
                  </h1>
                  <button onClick={() => { setNewName(user?.name || ''); setEditing(true); }}
                    className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                    data-testid="edit-name-button"
                  ><Pencil className="w-4 h-4 text-[#6B7280]" /></button>
                </div>
              )}

              <p className="text-sm text-[#6B7280] mb-3" data-testid="profile-email">{user?.email}</p>

              <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold text-white" style={{ background: 'rgb(var(--accent))' }} data-testid="profile-points">
                  <Coins className="w-4 h-4" />
                  {user?.points || 0} pts
                </div>

                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-xl text-sm text-[#6B7280]">
                  <User className="w-3.5 h-3.5" />
                  <span>Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '-'}</span>
                </div>

                {user?.auth_provider === 'google' && (
                  <span className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-xl text-sm font-medium">Google</span>
                )}
              </div>
            </div>

            {/* Referral Code */}
            {user?.referral_code && (
              <div className="text-center sm:text-right shrink-0" data-testid="referral-section">
                <p className="text-xs text-[#6B7280] mb-1 font-medium">Your Referral Code</p>
                <button onClick={copyReferral}
                  className="flex items-center gap-2 px-4 py-2 bg-[#FBBF24]/10 border-2 border-[#FBBF24]/30 rounded-xl hover:bg-[#FBBF24]/20 transition-colors"
                  data-testid="copy-referral-button"
                >
                  <span className="font-mono font-bold text-[#111827] tracking-wider">{user.referral_code}</span>
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-[#6B7280]" />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0EA5E9]"></div>
          </div>
        ) : stats && (
          <>
            <h2 className="text-xl font-black text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Your Stats</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" data-testid="stats-grid">
              {statCards.map((card, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow stat-card" data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                      <card.icon className="w-5 h-5" style={{ color: card.color }} />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">{card.label}</p>
                      <p className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{card.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Quiz History */}
            {stats.recent_history && stats.recent_history.length > 0 && (
              <>
                <h2 className="text-xl font-black text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Recent Quizzes</h2>
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="recent-history">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7280] uppercase">Quiz</th>
                          <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7280] uppercase">Score</th>
                          <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7280] uppercase">Time</th>
                          <th className="text-left px-5 py-3 text-xs font-bold text-[#6B7280] uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_history.map((h, idx) => {
                          const pct = h.total > 0 ? Math.round((h.score / h.total) * 100) : 0;
                          return (
                            <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`history-row-${idx}`}>
                              <td className="px-5 py-4">
                                <p className="font-bold text-[#111827] text-sm">{h.subject}</p>
                                <p className="text-xs text-[#6B7280]">{h.topic}</p>
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{h.score}/{h.total}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${pct >= 80 ? 'bg-green-100 text-green-700' : pct >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                    {pct}%
                                  </span>
                                </div>
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-1 text-sm text-[#6B7280]">
                                  <Clock className="w-3.5 h-3.5" />
                                  {h.time_taken}s
                                </div>
                              </td>
                              <td className="px-5 py-4 text-sm text-[#6B7280]">
                                {new Date(h.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {stats.total_quizzes === 0 && (
              <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                <Target className="w-12 h-12 text-[#E5E7EB] mx-auto mb-3" />
                <p className="text-[#6B7280] font-medium">No quizzes played yet. Join a pool to get started!</p>
              </div>
            )}
          </>
        )}

        {/* Danger Zone — account deletion (Play Store / DPDP compliance) */}
        <div className="mt-10 rounded-2xl border-2 border-red-100 bg-white overflow-hidden" data-testid="danger-zone">
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#111827] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>Delete my account</h3>
                <p className="text-sm text-[#6B7280]">
                  Permanently deletes your account, quiz history, points and personal data. Active subscriptions are cancelled. This cannot be undone.
                </p>
              </div>
              {!deleteOpen && (
                <button
                  onClick={() => setDeleteOpen(true)}
                  className="shrink-0 px-4 py-2 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold transition"
                  data-testid="delete-account-button"
                >
                  Delete…
                </button>
              )}
            </div>

            {deleteOpen && (
              <div className="mt-5 p-4 rounded-xl bg-red-50 border border-red-200 space-y-3" data-testid="delete-account-form">
                <div>
                  <label className="block text-xs font-bold text-red-700 mb-1.5">Type <span className="font-mono">DELETE</span> to confirm</label>
                  <input
                    value={deleteConfirm}
                    onChange={e => setDeleteConfirm(e.target.value)}
                    placeholder="DELETE"
                    className="w-full sm:w-64 px-3 py-2.5 border-2 border-red-200 rounded-xl focus:border-red-400 focus:outline-none font-mono text-sm bg-white"
                    data-testid="delete-confirm-input"
                  />
                </div>
                {!isGoogleUser && (
                  <div>
                    <label className="block text-xs font-bold text-red-700 mb-1.5">Your password</label>
                    <div className="relative w-full sm:w-64">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-red-300" />
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={e => setDeletePassword(e.target.value)}
                        placeholder="Current password"
                        className="w-full pl-9 pr-3 py-2.5 border-2 border-red-200 rounded-xl focus:border-red-400 focus:outline-none text-sm bg-white"
                        data-testid="delete-password-input"
                      />
                    </div>
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE' || (!isGoogleUser && !deletePassword)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition disabled:opacity-50"
                    data-testid="confirm-delete-button"
                  >
                    <Trash2 className="w-4 h-4" />
                    {deleting ? 'Deleting…' : 'Permanently delete my account'}
                  </button>
                  <button
                    onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); setDeletePassword(''); }}
                    className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-[#6B7280] text-sm font-bold hover:bg-slate-50 transition"
                    data-testid="cancel-delete-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
